import { alpha, createTheme, type Theme } from '@mui/material/styles';
import { type ThemeColors, DEFAULT_THEME } from './constants/themeDefaults';
import tinycolor from 'tinycolor2';

export const createAppTheme = (businessTheme?: ThemeColors): Theme => {
    // Helper function to validate color values
    const getValidColor = (value: string | undefined, defaultValue: string): string => {
        // Check if value is a valid color (not empty, null, or undefined)
        if (!value || typeof value !== 'string' || value.trim() === '') {
            console.warn(`Invalid color value detected: "${value}", using default: "${defaultValue}"`);
            return defaultValue;
        }
        return value;
    };

    // Ensure we have valid theme values by merging with DEFAULT_THEME and validating each color
    const theme: ThemeColors = {
        backgroundColor: getValidColor(businessTheme?.backgroundColor, DEFAULT_THEME.backgroundColor),
        primaryColor: getValidColor(businessTheme?.primaryColor, DEFAULT_THEME.primaryColor),
        secondaryColor: getValidColor(businessTheme?.secondaryColor, DEFAULT_THEME.secondaryColor),
        navigationColor: getValidColor(businessTheme?.navigationColor, DEFAULT_THEME.navigationColor),
        textColor: getValidColor(businessTheme?.textColor, DEFAULT_THEME.textColor),
        accentColor: getValidColor(businessTheme?.accentColor, DEFAULT_THEME.accentColor),
        subtleAccent: getValidColor(businessTheme?.subtleAccent, DEFAULT_THEME.subtleAccent),
        borderColor: getValidColor(businessTheme?.borderColor, DEFAULT_THEME.borderColor)
    };
    
    // Create darker hover color
    const darkerNavColor = tinycolor(theme.navigationColor)
        .darken(20)
        .toHexString();

    return createTheme({
        palette: {
            primary: {
                main: theme.primaryColor,
                light: theme.primaryColor,
                dark: theme.primaryColor,
            },
            secondary: {
                main: theme.secondaryColor,
                light: theme.secondaryColor,
                dark: theme.secondaryColor,
            },
            background: {
                default: theme.backgroundColor,
                paper: theme.backgroundColor,
            },
            text: {
                primary: theme.textColor,
                secondary: theme.textColor,
            },
            action: {
                active: theme.primaryColor,
                selected: `${theme.primaryColor}40`, // 40 is for 25% opacity
                focus: theme.primaryColor,
            },
        },
        typography: {
            fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h4: {
                fontWeight: 600,
            },
            h6: {
                fontWeight: 600,
            },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: 8,
                        '&:focus': {
                            boxShadow: `0 0 0 2px ${theme.accentColor}40`,
                        },
                        '&:hover': {
                            backgroundColor: `${theme.subtleAccent}`,
                        },
                        '&.MuiButton-contained': {
                            color: theme.secondaryColor, // E6 is hex for 90% opacity
                            '&.MuiButton-containedPrimary': {
                                color: theme.secondaryColor,
                            },
                            '&.MuiButton-containedSecondary': {
                                color: theme.secondaryColor,
                            },
                            '&:hover': {
                                backgroundColor: `${darkerNavColor}`,
                            },
                        },
                        '&.MuiButton-outlined': {
                            '&.MuiButton-outlinedPrimary': {
                                //color: theme.secondaryColor,
                            },
                            '&.MuiButton-outlinedSecondary': {
                                color: theme.secondaryColor,
                            },
                            '&:hover': {
                                backgroundColor: `${darkerNavColor}`,
                                color: theme.secondaryColor
                            },
                        },
                        '&.MuiButton-text': {
                            color: theme.secondaryColor,
                            '&.MuiButton-textPrimary': {
                                color: theme.secondaryColor,
                            },
                            '&.MuiButton-textSecondary': {
                                color: theme.secondaryColor,
                            },
                            '&:hover': {
                                backgroundColor:  `${darkerNavColor}`,
                            },
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: theme.navigationColor,
                        color: theme.secondaryColor,
                        borderRadius: 0,
                        '& .MuiIconButton-root': {
                            color: theme.secondaryColor,
                        },
                        '& .MuiButton-root': {
                            color: theme.secondaryColor,
                        },
                        '& .MuiTypography-root': {
                            color: theme.secondaryColor,
                        },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: `${theme.accentColor}40`,
                            '&:hover': {
                                backgroundColor: `${theme.accentColor}60`,
                            },
                        },
                        '&:hover': {
                            backgroundColor: `${theme.accentColor}20`,
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.accentColor,
                        },
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        backgroundColor: theme.accentColor,
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        backgroundColor: `${theme.accentColor}40`,
                    },
                    bar: {
                        backgroundColor: theme.accentColor,
                    },
                },
            },
            MuiCircularProgress: {
                styleOverrides: {
                    root: {
                        color: theme.navigationColor,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: theme.navigationColor,
                        color: theme.secondaryColor,
                        borderRadius: 0,
                        '& .MuiListItemIcon-root': {
                            color: theme.secondaryColor,
                        },
                        '& .MuiListItemText-primary': {
                            color: theme.textColor,
                        },
                    },
                },
            },
        },
    });
}; 